// client/src/components/marketer/project/modals/ContractModal.js
import React, { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../constants';
import '../../../../styles/marketer/modals/ContractModals.css';

function calcBaseOp(headcount) {
  const h = Number(headcount || 0);
  return Math.ceil(Math.max(0, h) / 3) * 5000;
}

export default function ContractModal({
  open,
  onClose,
  projectId,
  cover,
  gallery,
  recruit,
  reward,            // 5단계 값(인원/리워드)
  perReward,
  onStartCommit,     // 처리중 모달 오픈(부모에서 열기)
  onCommitSuccess,   // 커밋 성공(처리중 모달 닫고 DoneModal 오픈)
  onCommitFail,      // 커밋 실패(처리중 모달 닫고 안내)
}) {
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [points, setPoints] = useState(0);
  const [pointsLoading, setPointsLoading] = useState(false);

  // 로컬 계약금 계산(즉시)
  const headcount = Number(reward?.headcount || 0);
  const perCreator = Number(reward?.rewardPointPerCreator || 0);
  const rewardTotal = useMemo(() => headcount * perCreator, [headcount, perCreator]);
  const baseOpLocal = useMemo(() => calcBaseOp(headcount), [headcount]);
  const perBudgetLocal = useMemo(() => (perReward?.use ? Number(perReward?.budgetTotal || 0) : 0), [perReward]);
  const localDeposit = useMemo(() => baseOpLocal + rewardTotal + perBudgetLocal, [baseOpLocal, rewardTotal, perBudgetLocal]);

  useEffect(() => {
    if (!open) return;
    setAgree(false);
    setLoading(false);
    setPoints(0);
    setPointsLoading(true);

    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/marketer/mybiz/points/balance`, { credentials: 'include' });
        const j = await res.json().catch(() => ({}));
        if (res.ok && j?.ok) setPoints(Number(j.points || 0));
      } catch {}
      setPointsLoading(false);
    })();
  }, [open]);

  if (!open) return null;

  // 납부 시에만 이미지 업로드(필요한 경우)
  const uploadImagesIfNeeded = async () => {
    const needCover = cover instanceof File;
    const needGallery = Array.isArray(gallery) && gallery.some((f) => f instanceof File);
    if (!needCover && !needGallery) return;
    const fd = new FormData();
    if (needCover) fd.append('cover', cover);
    (gallery || []).forEach((f) => { if (f instanceof File) fd.append('gallery', f); });
    const r = await fetch(`${API_URL}/api/marketer/mybiz/projects/${encodeURIComponent(projectId)}/images`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok || !j?.ok) throw new Error(j?.message || '이미지 업로드 실패');
  };

  const commit = async () => {
    if (loading) return;
    if (pointsLoading || points < localDeposit) return; // 부족/로딩 중 차단

    setLoading(true);
    try { onStartCommit?.(); } catch {}

    try {
      await uploadImagesIfNeeded();
      const r = await fetch(`${API_URL}/api/marketer/mybiz/projects/${encodeURIComponent(projectId)}/contract/commit`, {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j?.ok) {
        const msg = j?.message === 'INSUFFICIENT_POINTS'
          ? '보유 포인트가 부족합니다. 충전 후 다시 시도해 주세요.'
          : (j?.message || '계약금 납부에 실패했습니다.');
        throw new Error(msg);
      }
      
      // ✅ 포인트 차감 성공 → 즉시 포인트 업데이트 이벤트 발생
      window.dispatchEvent(new CustomEvent('points-changed'));
      
      try { onCommitSuccess?.(); } catch {}
    } catch (e) {
      const msg = e?.message || '처리에 실패했습니다.';
      try { onCommitFail?.(msg); } catch {}
    }
    setLoading(false);
  };

  const dep = localDeposit;
  const baseOp = baseOpLocal;
  const perB = perBudgetLocal;

  const canPay = !pointsLoading && (points >= dep);
  const shortage = Math.max(0, dep - points);

  return (
    <div className="contract-modal mbm-overlay">
      <div className="contract-modal__content mbm">
        <button onClick={onClose} aria-label="close" className="contract-modal__close mbm-close">×</button>

        <div className="contract-modal__title mbm-title">프로젝트를 진행하시겠습니까?</div>
        <div className="contract-modal__subtitle">아래와 같은 순서로 프로젝트가 진행됩니다.</div>

        <div className="contract-modal__info-box">
          <div>프로젝트 만들기 및 모집 → 지원하기 → 선정하기 → 계약/콘텐츠 작성 → 결과물 승인 및 평가 → 광고비 지급</div>
          <div className="contract-modal__warning">계약이 진행되지 않을 시 광고비는 환불됩니다.</div>
          <div className="contract-modal__warning">CPS보증금의 경우, 미소진 된 금액은 프로젝트 종료 후 환불됩니다.</div>
          <div className="contract-modal__warning">모집 및 선정기간 동안 1명도 선정하지 않으면 프로젝트는 자동으로 취소됩니다.</div>
          <div className="contract-modal__warning--strong">계약금 납부 후 프로젝트 수정 및 취소는 불가합니다!</div>
        </div>

        <label className="contract-modal__checkbox">
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
          <span>(확인했습니다.)</span>
        </label>

        {(!pointsLoading) && (
          <div className="contract-modal__points-info">
            보유 포인트: <strong>{points.toLocaleString()}P</strong>
            {canPay ? (
              <span className="contract-modal__points-status--ok">(납부 가능)</span>
            ) : (
              <span className="contract-modal__points-status--low">(보유 포인트가 부족합니다. 부족액 {shortage.toLocaleString()}P)</span>
            )}
          </div>
        )}

        <div className="contract-modal__deposit-info">
          계약금: {Number(dep).toLocaleString()} Point
        </div>
        <div className="contract-modal__deposit-detail">
          기본 운영금 {Number(baseOp).toLocaleString()} + 결과물 리워드 {Number(rewardTotal).toLocaleString()} + CPS보증금 {Number(perB).toLocaleString()}
        </div>

        <div className="contract-modal__actions mbm-foot">
          <button type="button" onClick={onClose} className="mb-btn mb-btn-secondary">이전</button>
          <button
            type="button"
            onClick={commit}
            disabled={!agree || loading || pointsLoading || !canPay}
            className="mb-btn mb-btn-primary"
          >
            {loading ? '처리 중...' : '계약금 납부하기(완료)'}
          </button>
        </div>
      </div>
    </div>
  );
}